import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@mui/material';
import ChartModal from './ChartModal';
import { useAuth } from '../context/AuthContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ShowChartIcon from '@mui/icons-material/ShowChart';

interface VWAPSymbolData {
  symbol: string;
  last_update: string;
  position_type: string;
  current_price: number | null;
  vwap: number | null;
  distance_percent: number | null;
  bars_above: number | null;
  bars_below: number | null;
  entry_confidence: number | null;
  conditions_met: boolean;
  reasons: string[];
  detailed_analysis: any;
  momentum_analysis: any;
  total_volume: number | null;
  bars_count: number | null;
  vwap_slope: number | null;
  price_slope: number | null;
  // New DAILY_VWAP fields
  market_open_time?: string;
  current_time?: string;
  bars_fetched?: number;
  last_bar_time?: string;
  time_diff_seconds?: number;
}

interface VWAPMonitoringData {
  date: string;
  symbols: VWAPSymbolData[];
  active_symbols: string[];
  last_updated: string;
  total_symbols: number;
  source?: string;
}

interface PositionData {
  symbol: string;
  qty: number;
  market_value: number;
  unrealized_pl: number;
  unrealized_plpc: number;
  side: string;
}

interface V1VWAPMonitoringProps {
  instanceName: string;
}

const V1VWAPMonitoring: React.FC<V1VWAPMonitoringProps> = ({ instanceName }) => {
  const { token } = useAuth();
  const [data, setData] = useState<VWAPMonitoringData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  const [error, setError] = useState<string | null>(null);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [rawTail, setRawTail] = useState<string[] | null>(null);
  
  // Positions state
  const [positionsData, setPositionsData] = useState<PositionData[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [positionsError, setPositionsError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const endpoint = instanceName === 'LADYJANE' 
        ? `/api/instances/${instanceName}/ladyjane-monitoring` 
        : `/api/instances/${instanceName}/v1-vwap-monitoring`;
      const response = await fetch(endpoint, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      
      if (result.error) {
        setError(result.error);
      } else {
        setData(result);
        setError(null);
        setRawTail(result.raw_tail || null);
      }
    } catch (err) {
      setError('Failed to fetch VWAP monitoring data');
      console.error('Error fetching VWAP data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositionsData = async () => {
    try {
      setPositionsLoading(true);
      const response = await fetch(`/api/instances/${instanceName}/positions`, {
        headers: getAuthHeaders()
      });
      const result = await response.json();
      
      if (result.error) {
        setPositionsError(result.error);
      } else {
        setPositionsData(result.positions || []);
        setPositionsError(null);
      }
    } catch (err) {
      setPositionsError('Failed to fetch positions data');
      console.error('Error fetching positions:', err);
    } finally {
      setPositionsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPositionsData();
    
    // Set up auto-refresh every 5 seconds
    const interval = setInterval(() => {
      fetchData();
      fetchPositionsData();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [instanceName]);

  const handleSymbolClick = (symbol: string) => {
    setSelectedSymbol(symbol);
    setChartModalOpen(true);
  };

  const handleCloseChartModal = () => {
    setChartModalOpen(false);
    setSelectedSymbol('');
  };

  const getPositionTypeIcon = (positionType: string) => {
    if (positionType === 'LONG') {
      return <TrendingUpIcon sx={{ color: 'green', fontSize: 16 }} />;
    } else if (positionType === 'SHORT') {
      return <TrendingDownIcon sx={{ color: 'red', fontSize: 16 }} />;
    }
    return null;
  };

  const getConditionsIcon = (conditionsMet: boolean) => {
    return conditionsMet ? 
      <CheckCircleIcon sx={{ color: 'green', fontSize: 16 }} /> : 
      <CancelIcon sx={{ color: 'red', fontSize: 16 }} />;
  };

  const formatCurrency = (value: number | null): string => {
    if (value === null) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const formatPercent = (value: number | null): string => {
    if (value === null) return 'N/A';
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value: number | null): string => {
    if (value === null) return 'N/A';
    return value.toLocaleString();
  };

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading VWAP monitoring data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">
          Error loading VWAP monitoring data
        </Typography>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          API URL: {instanceName === 'LADYJANE' ? `/api/instances/${instanceName}/ladyjane-monitoring` : `/api/instances/${instanceName}/v1-vwap-monitoring`}
        </Typography>
      </Box>
    );
  }

  if (!data || data.symbols.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="h6">No VWAP monitoring data available</Typography>
        <Typography variant="body2" color="text.secondary">
          Waiting for V1 VWAP analysis data...
        </Typography>
        {rawTail && rawTail.length > 0 && (
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Raw Log Tail (debug)
            </Typography>
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
              {rawTail.join('\n')}
            </Box>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          {instanceName} VWAP Monitoring
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label="Auto-refresh: 10s" 
            color="primary" 
            size="small"
          />
          <IconButton 
            onClick={() => {fetchData(); fetchPositionsData();}} 
            disabled={loading}
            size="small"
          >
            <RefreshIcon className={loading ? 'spinning' : ''} />
          </IconButton>
        </Box>
      </Box>

      {/* Current Positions Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Current Positions
        </Typography>
        {positionsLoading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading positions...</Typography>
          </Box>
        ) : positionsError ? (
          <Typography color="error" variant="body2">
            Error: {positionsError}
          </Typography>
        ) : positionsData.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No open positions
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {positionsData.map((position) => (
              <Chip
                key={position.symbol}
                label={`${position.symbol}: ${position.qty > 0 ? '+' : ''}${position.qty} (${formatCurrency(position.unrealized_pl)})`}
                color={position.unrealized_pl >= 0 ? 'success' : 'error'}
                size="small"
                onClick={() => handleSymbolClick(position.symbol)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {/* Summary Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Symbols
              </Typography>
              <Typography variant="h4">
                {data.total_symbols}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Conditions Met
              </Typography>
              <Typography variant="h4">
                {data.symbols.filter(s => s.conditions_met).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                LONG Positions
              </Typography>
              <Typography variant="h4">
                {data.symbols.filter(s => s.position_type === 'LONG').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                SHORT Positions
              </Typography>
              <Typography variant="h4">
                {data.symbols.filter(s => s.position_type === 'SHORT').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* VWAP Monitoring Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>VWAP</TableCell>
                <TableCell>Distance</TableCell>
                <TableCell>Bars A/B</TableCell>
                <TableCell>Bars Fetched</TableCell>
                <TableCell>Time Diff</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Conditions</TableCell>
                <TableCell>Volume</TableCell>
                <TableCell>Last Update</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.symbols.map((symbol) => (
                <TableRow key={symbol.symbol} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleSymbolClick(symbol.symbol)}
                    >
                      {symbol.symbol}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getPositionTypeIcon(symbol.position_type)}
                      <Typography variant="body2">
                        {symbol.position_type || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{formatCurrency(symbol.current_price)}</TableCell>
                  <TableCell>{formatCurrency(symbol.vwap)}</TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={symbol.distance_percent && symbol.distance_percent > 0 ? 'success.main' : 'error.main'}
                    >
                      {formatPercent(symbol.distance_percent)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {symbol.bars_above}/{symbol.bars_below}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {symbol.bars_fetched || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {symbol.time_diff_seconds ? `${symbol.time_diff_seconds.toFixed(1)}s` : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {symbol.entry_confidence?.toFixed(2) || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getConditionsIcon(symbol.conditions_met)}
                      <Typography variant="body2">
                        {symbol.conditions_met ? 'Met' : 'Not Met'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatNumber(symbol.total_volume)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {symbol.last_update ? new Date(symbol.last_update).toLocaleTimeString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  {/* Chart column removed since symbol is clickable */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Reasons Details */}
      {data.symbols.some(s => s.reasons && s.reasons.length > 0) && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Entry Condition Details
          </Typography>
          {data.symbols
            .filter(s => s.reasons && s.reasons.length > 0)
            .map((symbol) => (
              <Box key={symbol.symbol} mb={2}>
                <Typography variant="subtitle2" color="primary">
                  {symbol.symbol}:
                </Typography>
                <Box ml={2}>
                  {symbol.reasons.map((reason, index) => (
                    <Typography key={index} variant="body2" color="text.secondary">
                      • {reason}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
        </Paper>
      )}

      {/* Chart Modal */}
      <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChartModal}
        symbol={selectedSymbol}
      />

      <style>
        {`
          .spinning {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default V1VWAPMonitoring;